import { CodeOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { useState } from 'react';

import {
  previewESignInfo,
  type ESignInfoProbeResult,
  type ESignSubmission,
} from '@/api/applications';
import { isDevLoginEnabled } from '@/auth/devLogin';
import { extractApiError } from '@/originator/statusUtils';

/**
 * True where the probe button may render: localhost and Dev only.
 *
 * Reuses the dev-login flag rather than introducing a second one. That flag is written into
 * `config.json` by the Dev pipeline alone — UAT's config carries no `auth` block and Prod's never
 * will — so it already means exactly "this is a developer environment".
 *
 * <p>This is the COSMETIC half of the gate. The real one is server-side: the endpoint is mapped
 * only where `BmoDiagnostics.IsEnabled` holds, which fails closed on any environment name nobody
 * anticipated. Hiding the button is what stops a reviewer being confused by a control that 404s;
 * it is not what stops the route being reachable.</p>
 */
export function isESignInfoProbeEnabled(): boolean {
  return isDevLoginEnabled();
}

/** Console group label, so the output is findable in a noisy devtools log. */
const LOG_LABEL = '[eSignInfo probe]';

/**
 * TODO(BMO-PRELAUNCH-DIAGNOSTICS): remove with the endpoint before go-live.
 *
 * Dev-only button on the PDF Generation step that logs the `eSignInfo` block this application
 * would send — the last stop before submit, alongside the generated documents the block names.
 * Rendered in originator / approver preview too, where the roster resolves the same way.
 *
 * <h3>Console, not a modal</h3>
 * Deliberately the smallest surface that does the job, because this whole feature gets deleted
 * before go-live. Devtools already renders an expandable object tree better than a `<pre>` would,
 * and logging costs no modal, no result state and no layout — so removing this later is deleting
 * one file and two lines of `FormShell`.
 *
 * <h3>Why it asks the server</h3>
 * Almost nothing interesting here is knowable in the browser. The roster and the uploaded-file list
 * are ours, but signer ids (`applicant-1`), the 0-based `index`, the `{applicant-1.ANCHOR}`
 * bindings, the interleaved date fields and the §14.1 delivery names are all composed by
 * `BmoDataBuilder` server-side and deliberately kept out of the client's reach (see
 * `buildESignSubmission`). Rebuilding them here would be a second source of truth that drifts, and
 * a probe showing a payload BMO never receives is worse than no probe at all.
 *
 * <h3>It reads; it never writes</h3>
 * Unlike the save-application connectivity probe next door — which mints a real record at BMO on
 * every run — this call composes and returns. No BMO request, no ISAM token spent, no S3 write.
 * That is what makes it safe to offer in preview, where writing an application would be a bug.
 */
export function ESignInfoProbe({
  templateId,
  subsequentTemplateId,
  language,
  applicationId,
  creationTimestamp,
  bmoStatus,
  buildSubmission,
}: {
  /** The BASE workflow — what identifies the application, branch or no branch. */
  templateId: string;
  /**
   * The workflow a form branch swapped in, when one did; null otherwise.
   *
   * The e-signature config, the PDF entries it places signatures in and therefore every document
   * `eSignInfo` names live on THAT form's steps — the base form no longer contributes them. Sent
   * so the probe composes against the same template the next save will (`ApplicationEndpoints`'
   * `branchTemplate ?? template`); without it the button reports the base form's documents, which
   * is precisely the "payload BMO never receives" this diagnostic exists to rule out.
   */
  subsequentTemplateId?: string | null;
  /** The PDF language, not the advisor's UI locale — it picks the placements and the signer locale. */
  language: 'en' | 'fr';
  /** Absent in preview, and in the advisor runtime until the first save returns one. */
  applicationId?: string;
  /** Absent likewise; the server clock stands in, and the log says so. */
  creationTimestamp?: string;
  /**
   * The shell's current rung on BMO's ladder. Sent because the server gates `eSignInfo` on it —
   * a probe that composed the block unconditionally would show a payload the next save omits.
   * Reaching this step means Next'ing off the e-signature step, so it normally reads
   * ESIGNER-REVIEW-COMPLETE or higher in both the runtime and preview.
   */
  bmoStatus: string;
  /**
   * Resolves the roster + uploaded documents as of RIGHT NOW.
   *
   * A callback rather than a value because the submission is a function of the current answers —
   * the same reason `persistSnapshot` rebuilds it on every save instead of holding one in state.
   * Passing a prebuilt object would let the probe log a roster the next save would not send.
   */
  buildSubmission: () => ESignSubmission;
}) {
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const result = await previewESignInfo({
        templateId,
        subsequentTemplateId,
        language,
        applicationId,
        creationTimestamp,
        // Minted here, as a real press would. Supporting-document names are stamped with the
        // press instant now, so a probe sending none would show the fallback stamp instead of
        // what the next save actually puts on the wire.
        deliveryTimestamp: new Date().toISOString(),
        bmoStatus,
        eSign: buildSubmission(),
      });
      log(result, language);
      void message.success(`${LOG_LABEL} logged to the console.`);
    } catch (err) {
      // The endpoint's own refusal code is the useful part — INVALID_SUPPORTING_DOC here means the
      // next real save fails the same way. Surface it verbatim rather than a generic failure.
      const { status, code, message: detail } = extractApiError(err);
      const reason =
        status === 404
          ? 'The probe is not enabled on this environment (BmoDiagnostics is off).'
          : [code, detail].filter(Boolean).join(' — ') || 'The probe request failed.';
      console.error(LOG_LABEL, reason, err);
      void message.error(`${LOG_LABEL} ${reason}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Button icon={<CodeOutlined />} loading={loading} onClick={() => void run()}>
        Log eSignInfo to console (dev)
      </Button>
    </div>
  );
}

/**
 * Both forms, because they serve different jobs: the live object is what devtools lets you expand
 * and drill into, and the JSON string is what you copy out to paste somewhere else.
 */
function log(result: ESignInfoProbeResult, language: 'en' | 'fr'): void {
  /* eslint-disable no-console -- The console IS this diagnostic's output surface; the repo's
     warn/error allowance exists to keep stray logs out of shipped code, which is a different
     thing. Scoped to this function so the rule still applies to the component above, and the
     whole file is deleted with the probe endpoint before go-live. */
  console.group(LOG_LABEL);
  console.log(
    'applicationId=%s%s · FileX env=%s · language=%s',
    result.applicationId,
    // Preview has no application, so the §14.1 names are shaped correctly but stamped with a
    // marker id and a server-clock timestamp. Saying so stops anyone hunting for a record at BMO.
    result.isPreviewApplicationId ? ' (marker — no application exists yet)' : '',
    result.fileXEnvironment,
    language,
  );

  if (result.bmoData) {
    console.log(result.bmoData);
    console.log(JSON.stringify(result.bmoData, null, 2));
    // Expected on every save before the advisor passes the e-signature step: applicantInfo ships
    // as soon as a signatory has details typed, while the tagging spec waits for the step that
    // fills the roster in. Said out loud so an operator can tell that apart from a broken config.
    if (result.eSignInfoOmittedReason) {
      console.warn('eSignInfo is omitted:', result.eSignInfoOmittedReason);
    }
    if (result.emptyDocumentsReason) {
      console.warn('eSignInfo.documents is empty:', result.emptyDocumentsReason);
    }
  } else {
    // The normal state early in an application's life, not an error — with no roster there is no
    // applicant to describe and no signer id for a placement to bind to.
    console.warn(
      'bmoData is omitted from the save:',
      result.omittedReason ?? 'the builder returned no payload.',
    );
  }

  console.groupEnd();
}
